import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../../api/apiServices";

// ── Formatters ────────────────────────────────────────────────
const fmt  = (n) => Number(n || 0).toLocaleString("en-NG");
const fmtK = (n) => {
  n = Number(n || 0);
  if (n >= 1_000_000) return `₦${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `₦${(n / 1_000).toFixed(1)}K`;
  return `₦${fmt(n)}`;
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short" }) : "—";

// ── SVG bar chart (no library) ────────────────────────────────
function MiniBarChart({ data = [], color = "#00C896", valueKey = "count", height = 56 }) {
  if (!data.length) return <div className="flex items-center justify-center text-white/15 text-xs" style={{ height }}>No data</div>;
  const values = data.map(d => Number(d[valueKey] || d.volume || d.amount || d.dau || d.count || 0));
  const max    = Math.max(...values, 1);
  const W = 100, H = height;
  const bw = Math.max(1, W / values.length - 0.8);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height }} preserveAspectRatio="none">
      {values.map((v, i) => {
        const h = Math.max(2, (v / max) * H);
        return <rect key={i} x={i * (W / values.length)} y={H - h} width={bw} height={h} fill={color} opacity={0.3 + (v / max) * 0.7} rx="1" />;
      })}
    </svg>
  );
}

// ── Metric card ───────────────────────────────────────────────
function MetricCard({ label, value, sub, icon, color, warn, trend, onClick }) {
  return (
    <div onClick={onClick}
      className={`bg-[#161616] rounded-xl border p-4 ${warn ? "border-red-500/20" : "border-white/5"} ${onClick ? "cursor-pointer hover:bg-white/3 transition-colors" : ""}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: (color || "#00C896") + "18" }}>
          <i className={`${icon} text-sm`} style={{ color: color || "#00C896" }} />
        </div>
        {trend !== undefined && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${trend >= 0 ? "bg-[#00C896]/10 text-[#00C896]" : "bg-red-500/10 text-red-400"}`}>
            {trend >= 0 ? "+" : ""}{trend}%
          </span>
        )}
      </div>
      <p className="text-white font-bold text-xl leading-none mb-1">{value}</p>
      <p className="text-white/40 text-[10px] uppercase tracking-wide">{label}</p>
      {sub && <p className="text-white/25 text-[10px] mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Donut chart (SVG) ─────────────────────────────────────────
function DonutChart({ slices = [], size = 80 }) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (!total) return <div className="flex items-center justify-center text-white/15 text-xs" style={{ width: size, height: size }}>—</div>;
  let offset = 0;
  const cx = size / 2, cy = size / 2, r = size * 0.38, stroke = size * 0.16;
  const circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} className="flex-shrink-0">
      {slices.map((s, i) => {
        const pct  = s.value / total;
        const dash = pct * circ;
        const el   = (
          <circle key={i} cx={cx} cy={cy} r={r}
            fill="none" stroke={s.color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={-offset * circ}
            style={{ transform: "rotate(-90deg)", transformOrigin: "center" }} />
        );
        offset += pct;
        return el;
      })}
    </svg>
  );
}

// ── Horizontal bar ────────────────────────────────────────────
function HBar({ label, value, total, color = "#00C896", sub }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-white/60 truncate flex-1 mr-2">{label}</span>
        <span className="text-white font-semibold flex-shrink-0">{fmt(value)}</span>
        <span className="text-white/30 w-8 text-right flex-shrink-0">{pct}%</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
      {sub && <p className="text-white/20 text-[9px]">{sub}</p>}
    </div>
  );
}

// ── Section header ────────────────────────────────────────────
function SectionLabel({ label }) {
  return <p className="text-white/25 text-[10px] uppercase tracking-widest font-bold mb-3">{label}</p>;
}

// ── Referral tree panel (unchanged from before) ───────────────
function TreeNode({ user, depth = 0, expanded, onToggle }) {
  const hasChildren = user.children?.length > 0;
  const isExpanded  = expanded[user.userId];
  const KYC_COLOR   = { approved: "#00C896", submitted: "#D97706", rejected: "#DC2626", not_started: "#64748B" };
  return (
    <div className={depth > 0 ? "ml-5 border-l border-white/8 pl-3" : ""}>
      <div className="flex items-center gap-2.5 py-2 group">
        <button onClick={() => hasChildren && onToggle(user.userId)}
          className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${hasChildren ? "hover:bg-white/10 cursor-pointer" : "cursor-default"}`}>
          {hasChildren
            ? <i className={`text-white/40 text-xs ${isExpanded ? "ri-subtract-line" : "ri-add-line"}`} />
            : <div className="w-1.5 h-1.5 rounded-full bg-white/15" />}
        </button>
        <div className="w-7 h-7 rounded-full bg-[#00C896]/10 flex items-center justify-center flex-shrink-0">
          <span className="text-[#00C896] text-[10px] font-bold">{(user.userName || user.email || "?")[0].toUpperCase()}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-white text-xs font-medium truncate">{user.userName || "—"}</p>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
              style={{ background: (KYC_COLOR[user.kycStatus] || "#64748B") + "18", color: KYC_COLOR[user.kycStatus] || "#64748B" }}>KYC</span>
          </div>
          <p className="text-white/30 text-[10px] truncate">{user.email}</p>
        </div>
        <div className="flex-shrink-0 text-right">
          {user.referredUsers?.length > 0 && <p className="text-[#00C896] text-[10px] font-semibold">{user.referredUsers.length} ref</p>}
          <p className="text-white/20 text-[9px]">{fmtDate(user.createdAt)}</p>
        </div>
      </div>
      {hasChildren && isExpanded && user.children.map(child =>
        <TreeNode key={child.userId} user={child} depth={depth + 1} expanded={expanded} onToggle={onToggle} />
      )}
    </div>
  );
}

function ReferralTreePanel({ onClose }) {
  const [search, setSearch]     = useState("");
  const [users, setUsers]       = useState([]);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [treeData, setTreeData] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [timer, setTimer]       = useState(null);

  const fetchList = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/mtrade/admin/referrals?search=${encodeURIComponent(q)}&limit=15`);
      if (res.data.status) { setUsers(res.data.data); setStats(res.data.stats); }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(); }, []);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(timer);
    setTimer(setTimeout(() => fetchList(val), 350));
  };

  const loadTree = async (userId) => {
    setTreeLoading(true);
    try {
      const res = await apiClient.get(`/mtrade/admin/referrals/${userId}/tree`);
      if (res.data.status) { setTreeData(res.data.data); setExpanded({}); }
    } catch { /* silent */ }
    finally { setTreeLoading(false); }
  };

  useEffect(() => {
    if (treeData?.tree?.length) {
      const init = {};
      treeData.tree.forEach(n => { init[n.userId] = true; });
      setExpanded(init);
    }
  }, [treeData]);

  const REWARD_LABEL = { referral: "Referral Signup", referral_signup: "Referral Signup", first_referral: "First Referral Bonus" };

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/50" onClick={onClose} />
      <div className="w-full max-w-xl bg-[#0f0f0f] flex flex-col shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 flex-shrink-0">
          <div>
            <p className="text-white font-bold">Referral Tree Viewer</p>
            {stats && <p className="text-white/30 text-xs mt-0.5">{fmt(stats.totalReferrals)} total · ₦{fmt(stats.totalReferralRewards)} paid</p>}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10"><i className="ri-close-line text-white/60" /></button>
        </div>
        <div className="px-6 py-4 border-b border-white/5 flex-shrink-0">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
            <input value={search} onChange={e => handleSearch(e.target.value)} placeholder="Email, username or referral code…"
              className="w-full bg-[#161616] border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {treeData ? (
            <div className="px-6 py-4 space-y-4">
              <button onClick={() => setTreeData(null)} className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors">
                <i className="ri-arrow-left-line" /> Back
              </button>
              <div className="bg-white/4 rounded-xl border border-white/5 p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#00C896]/10 flex items-center justify-center">
                    <span className="text-[#00C896] font-bold text-sm">{(treeData.root.userName || treeData.root.email || "?")[0].toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">{treeData.root.userName || "—"}</p>
                    <p className="text-white/40 text-xs">{treeData.root.email}</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-[#00C896] text-xs font-bold font-mono">{treeData.root.referralCode}</p>
                    <p className="text-white/30 text-[10px]">Referral code</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[["Direct", treeData.totalL1], ["Level 2", treeData.totalL2], ["Total", treeData.totalL1 + treeData.totalL2]].map(([label, value]) => (
                    <div key={label} className="bg-white/4 rounded-lg py-2 px-1">
                      <p className="text-white font-bold text-base">{value}</p>
                      <p className="text-white/30 text-[9px]">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
              {treeLoading ? (
                <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#00C896]/30 border-t-[#00C896] rounded-full animate-spin" /></div>
              ) : treeData.tree.length === 0 ? (
                <div className="text-center py-8"><i className="ri-group-line text-white/15 text-3xl mb-2 block" /><p className="text-white/25 text-sm">No referrals yet</p></div>
              ) : (
                <div className="bg-white/4 rounded-xl border border-white/5 p-4">
                  <p className="text-white/40 text-[10px] uppercase tracking-wide mb-3">Network</p>
                  {treeData.tree.map(node => <TreeNode key={node.userId} user={node} depth={0} expanded={expanded} onToggle={(id) => setExpanded(p => ({ ...p, [id]: !p[id] }))} />)}
                </div>
              )}
              {treeData.rewardEvents?.length > 0 && (
                <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                  <p className="text-white/40 text-[10px] uppercase tracking-wide px-4 py-3 border-b border-white/5">Rewards Earned</p>
                  {treeData.rewardEvents.slice(0, 8).map((ev, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-white/4 last:border-0">
                      <div><p className="text-white/60 text-xs">{REWARD_LABEL[ev.type] || ev.type}</p><p className="text-white/25 text-[10px]">{fmtDate(ev.createdAt)}</p></div>
                      <p className="text-[#00C896] text-sm font-bold">+₦{fmt(ev.amount)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="divide-y divide-white/4">
              {loading ? [...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-6 py-4">
                  <div className="w-8 h-8 rounded-full bg-white/5 animate-pulse" />
                  <div className="flex-1 space-y-2"><div className="h-3 w-28 bg-white/5 rounded animate-pulse" /><div className="h-2.5 w-20 bg-white/5 rounded animate-pulse" /></div>
                </div>
              )) : users.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-14"><i className="ri-team-line text-white/15 text-4xl mb-2" /><p className="text-white/25 text-sm">No referrers found</p></div>
              ) : users.map(user => (
                <button key={user.userId} onClick={() => loadTree(user.userId)}
                  className="w-full flex items-center gap-3 px-6 py-4 hover:bg-white/3 transition-colors text-left">
                  <div className="w-8 h-8 rounded-full bg-[#00C896]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#00C896] text-xs font-bold">{(user.userName || user.email || "?")[0].toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{user.userName || "—"}</p>
                    <p className="text-white/30 text-xs truncate">{user.email}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[#00C896] text-xs font-bold">{user.referredUsers?.length || 0}</span>
                    <i className="ri-arrow-right-s-line text-white/20" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────
// Main OverviewView
// ─────────────────────────────────────────────────────────────
export default function OverviewView() {
  const [overview, setOverview]       = useState(null);
  const [analytics, setAnalytics]     = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [range, setRange]             = useState("30d");
  const [uaRange, setUaRange]         = useState("30d");
  const [loading, setLoading]         = useState(true);
  const [aLoading, setALoading]       = useState(false);
  const [uaLoading, setUaLoading]     = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [activeChart, setActiveChart] = useState("revenue");

  const fetchOverview = useCallback(async () => {
    try {
      const res = await apiClient.get("/mtrade/admin/overview");
      if (res.data.status) setOverview(res.data.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  const fetchAnalytics = useCallback(async (r) => {
    setALoading(true);
    try {
      const res = await apiClient.get(`/mtrade/admin/analytics?range=${r}`);
      if (res.data.status) setAnalytics(res.data.data);
    } catch { /* silent */ }
    finally { setALoading(false); }
  }, []);

  const fetchUserAnalytics = useCallback(async (r) => {
    setUaLoading(true);
    try {
      const res = await apiClient.get(`/mtrade/admin/user-analytics?range=${r}`);
      if (res.data.status) setUserAnalytics(res.data.data);
    } catch { /* silent */ }
    finally { setUaLoading(false); }
  }, []);

  useEffect(() => { fetchOverview(); }, []);
  useEffect(() => { fetchAnalytics(range); }, [range]);
  useEffect(() => { fetchUserAnalytics(uaRange); }, [uaRange]);

  const u  = overview?.users      || {};
  const r  = overview?.revenue    || {};
  const b  = overview?.bills      || {};
  const rw = overview?.rewards    || {};
  const eng = overview?.engagement || {};
  const bd  = overview?.breakdowns || {};
  const nm = overview?.newModules || {};

  const REWARD_LABEL = {
    cashback: "Cashback", milestone: "Milestone", referral: "Referral",
    referral_signup: "Referral", first_referral: "First Referral",
    streak: "Streak", admin_bonus: "Admin Bonus",
    first_deposit: "First Deposit", first_buy: "First Buy",
  };

  // KYC funnel ordered
  const KYC_ORDER  = ["approved","under_review","submitted","info_requested","rejected","not_started"];
  const KYC_COLORS = { approved: "#00C896", under_review: "#0284C7", submitted: "#D97706", info_requested: "#7C3AED", rejected: "#DC2626", not_started: "#64748B" };
  const KYC_LABELS = { approved: "Approved", under_review: "In Review", submitted: "Pending", info_requested: "Info Needed", rejected: "Rejected", not_started: "Not Started" };

  const kycFunnel = bd.kycFunnel
    ? KYC_ORDER.map(k => bd.kycFunnel.find(x => x._id === k)).filter(Boolean)
    : [];
  const kycTotal  = kycFunnel.reduce((s, x) => s + x.count, 0);

  const ua = userAnalytics || {};

  if (loading) return (
    <div className="space-y-5">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[...Array(4)].map((_, j) => <div key={j} className="h-24 bg-[#161616] rounded-xl border border-white/5 animate-pulse" />)}
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">Dashboard Overview</h2>
          <p className="text-white/40 text-xs mt-0.5">Real-time platform metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowReferral(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-white/5 border border-white/8 rounded-xl text-xs text-white/60 hover:text-white hover:bg-white/10 transition-colors">
            <i className="ri-share-line text-sm" /> Referrals
          </button>
          <button onClick={() => { fetchOverview(); fetchAnalytics(range); fetchUserAnalytics(uaRange); }}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-colors">
            <i className="ri-refresh-line text-sm" />
          </button>
        </div>
      </div>

      {/* ── USERS ── */}
      <div>
        <SectionLabel label="Users" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard label="Total Users"    value={fmt(u.total)}        icon="ri-group-line"        color="#00C896" sub={`+${u.newToday || 0} today`} />
          <MetricCard label="New This Month" value={fmt(u.newThisMonth)} icon="ri-user-add-line"     color="#0284C7" trend={u.monthGrowth} />
          <MetricCard label="KYC Approved"   value={fmt(u.kycApproved)}  icon="ri-shield-check-line" color="#059669" sub={`${u.pendingKYC || 0} pending`} warn={u.pendingKYC > 0} />
          <MetricCard label="Suspended"      value={fmt(u.suspended)}    icon="ri-forbid-line"       color="#DC2626" warn={u.suspended > 0} />
        </div>
      </div>

      {/* ── ENGAGEMENT ── */}
      <div>
        <SectionLabel label="Engagement" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard label="Active (7d)"     value={fmt(eng.activeUsersWeek)}  icon="ri-user-star-line"   color="#00C896" sub="distinct users transacted" />
          <MetricCard label="Active (30d)"    value={fmt(eng.activeUsersMonth)} icon="ri-calendar-check-line" color="#0284C7" sub={`${eng.retentionRate || 0}% retention rate`} />
          <MetricCard label="Returning Users" value={fmt(eng.returningUsers)}   icon="ri-repeat-line"      color="#7C3AED" sub=">1 transaction ever" />
          <MetricCard label="Activation Rate" value={`${eng.activationRate || 0}%`} icon="ri-rocket-line"  color="#F97316" sub={`${fmt(eng.usersWithNoTx)} never transacted`} warn={eng.activationRate < 50} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <MetricCard label="Avg Tx / User (30d)"  value={ua.sessionGap?.avgDaysBetweenTx ?? "—"} icon="ri-exchange-line"   color="#00C896" sub="avg days between transactions" />
          <MetricCard label="Inactive 30d"  value={fmt(ua.churn?.inactive30)}  icon="ri-user-unfollow-line" color="#D97706" warn={(ua.churn?.inactive30 || 0) > 100} sub="at risk of churn" />
          <MetricCard label="Inactive 60d"  value={fmt(ua.churn?.inactive60)}  icon="ri-ghost-line"         color="#DC2626" warn={(ua.churn?.inactive60 || 0) > 50} />
          <MetricCard label="Inactive 90d"  value={fmt(ua.churn?.inactive90)}  icon="ri-skull-line"         color="#64748B" />
        </div>
      </div>

      {/* ── REVENUE ── */}
      <div>
        <SectionLabel label="Revenue (This Month)" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard label="Total Volume"  value={fmtK(r.monthlyVolume)} icon="ri-exchange-line"          color="#00C896" sub={`${r.txCount} transactions`} />
          <MetricCard label="Platform Fees" value={fmtK(r.monthlyFees)}   icon="ri-percent-line"           color="#7C3AED" sub={`₦${fmt(r.totalFees)} all-time`} />
          <MetricCard label="Buy Volume"    value={fmtK(r.buyVolume)}     icon="ri-arrow-down-line"        color="#059669" />
          <MetricCard label="Sell Volume"   value={fmtK(r.sellVolume)}    icon="ri-arrow-up-line"          color="#DC2626" />
        </div>
      </div>

      {/* ── BILLS + REWARDS ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Bills Volume"   value={fmtK(b.monthlyVolume)}  icon="ri-receipt-line"           color="#D97706" sub={`${b.monthlyCount} bills paid`} />
        <MetricCard label="Rewards Issued" value={fmtK(rw.monthlyIssued)} icon="ri-gift-line"              color="#F97316" sub={`${rw.monthlyCount} events`} />
        <MetricCard label="Deposits"       value={fmtK(r.depositVolume)}  icon="ri-arrow-down-circle-line" color="#0284C7" />
        <MetricCard label="All-time Fees"  value={fmtK(r.totalFees)}      icon="ri-bank-line"              color="#00C896" />
      </div>

      {/* ── NEW MODULES ── */}
      {overview?.newModules && (
        <div>
          <SectionLabel label="Savings · Transfers · Links · Payroll" />

          {/* Savings */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricCard label="Savings Held"    value={fmtK(nm.savings?.totalHeld)}     icon="ri-safe-2-line"      color="#00C896" sub={`${fmt(nm.savings?.activeGoals)} active goals`} />
            <MetricCard label="Saved This Month" value={fmtK(nm.savings?.monthDeposits)} icon="ri-arrow-down-line"  color="#0284C7" sub={`${fmt(nm.savings?.depositCount)} deposits`} />
            <MetricCard label="Transfers (mo)"  value={fmtK(nm.transfers?.monthVolume)}  icon="ri-send-plane-line"  color="#7C3AED" sub={`${fmt(nm.transfers?.monthCount)} sent`} />
            <MetricCard label="Link Payments"   value={fmtK(nm.paymentLinks?.monthReceived)} icon="ri-link"        color="#059669" sub={`${fmt(nm.paymentLinks?.monthCount)} received`} />
          </div>

          {/* Payroll + companies + support */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            <MetricCard label="Payroll (mo)"   value={fmtK(nm.payroll?.monthDisbursed)} icon="ri-team-line"      color="#0284C7" sub={`${fmt(nm.payroll?.monthRuns)} runs`} />
            <MetricCard label="Companies"      value={fmt(nm.companies?.total)}         icon="ri-building-line"  color="#00C896" sub={`${fmt(nm.companies?.activeStaff)} active staff`} />
            <MetricCard label="Payment Links"  value={fmt(nm.paymentLinks?.totalLinks)} icon="ri-links-line"     color="#7C3AED" sub="total created" />
            <MetricCard label="Open Disputes"  value={fmt(nm.support?.openDisputes)}    icon="ri-alarm-warning-line" color="#DC2626" warn={(nm.support?.openDisputes || 0) > 0} sub={`${fmt(nm.support?.pendingTickets)} pending tickets`} />
          </div>
        </div>
      )}

      {/* ── REVENUE ANALYTICS CHART ── */}
      <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex gap-1">
            {[{ key: "revenue", label: "Revenue", color: "#00C896" }, { key: "bills", label: "Bills", color: "#7C3AED" }, { key: "rewards", label: "Rewards", color: "#F97316" }, { key: "signups", label: "Signups", color: "#0284C7" }].map(c => (
              <button key={c.key} onClick={() => setActiveChart(c.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeChart === c.key ? "text-black" : "text-white/30 hover:text-white/60"}`}
                style={activeChart === c.key ? { background: c.color } : {}}>
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {["7d", "30d", "90d"].map(rv => (
              <button key={rv} onClick={() => setRange(rv)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all ${range === rv ? "bg-white/10 text-white" : "text-white/30 hover:text-white/50"}`}>
                {rv}
              </button>
            ))}
          </div>
        </div>
        <div className="px-5 pt-4 pb-2">
          {aLoading
            ? <div className="h-20 bg-white/5 rounded-xl animate-pulse" />
            : <MiniBarChart data={(analytics?.series || {})[activeChart] || []} color={{ revenue: "#00C896", bills: "#7C3AED", rewards: "#F97316", signups: "#0284C7" }[activeChart]} valueKey={{ revenue: "volume", bills: "volume", rewards: "amount", signups: "count" }[activeChart]} />
          }
        </div>
        <div className="px-5 pb-4 grid grid-cols-3 gap-3 text-center">
          {activeChart === "revenue" && [
            { label: "Volume",       v: fmtK((analytics?.series?.revenue || []).reduce((s, d) => s + (d.volume || 0), 0)) },
            { label: "Fees",         v: fmtK((analytics?.series?.revenue || []).reduce((s, d) => s + (d.fees || 0), 0)) },
            { label: "Transactions", v: fmt((analytics?.series?.revenue  || []).reduce((s, d) => s + (d.count || 0), 0)) },
          ].map(c => <div key={c.label} className="bg-white/4 rounded-lg py-2"><p className="text-white font-bold text-sm">{c.v}</p><p className="text-white/25 text-[9px]">{c.label}</p></div>)}
          {activeChart === "bills" && [
            { label: "Volume",       v: fmtK((analytics?.series?.bills || []).reduce((s, d) => s + (d.volume || 0), 0)) },
            { label: "Transactions", v: fmt((analytics?.series?.bills  || []).reduce((s, d) => s + (d.count || 0), 0)) },
            { label: "Top Type",     v: analytics?.breakdown?.bills?.[0]?._id || "—" },
          ].map(c => <div key={c.label} className="bg-white/4 rounded-lg py-2"><p className="text-white font-bold text-sm">{c.v}</p><p className="text-white/25 text-[9px]">{c.label}</p></div>)}
          {activeChart === "rewards" && [
            { label: "Issued",   v: fmtK((analytics?.series?.rewards || []).reduce((s, d) => s + (d.amount || 0), 0)) },
            { label: "Events",   v: fmt((analytics?.series?.rewards  || []).reduce((s, d) => s + (d.count || 0), 0)) },
            { label: "Top Type", v: REWARD_LABEL[analytics?.breakdown?.rewards?.[0]?._id] || "—" },
          ].map(c => <div key={c.label} className="bg-white/4 rounded-lg py-2"><p className="text-white font-bold text-sm">{c.v}</p><p className="text-white/25 text-[9px]">{c.label}</p></div>)}
          {activeChart === "signups" && [
            { label: "New Users", v: fmt((analytics?.series?.signups || []).reduce((s, d) => s + (d.count || 0), 0)) },
            { label: "Avg/Day",   v: (analytics?.series?.signups?.length || 0) > 0 ? fmt(Math.round((analytics.series.signups.reduce((s, d) => s + d.count, 0)) / analytics.series.signups.length)) : "0" },
            { label: "Peak Day",  v: fmt(Math.max(...(analytics?.series?.signups?.map(d => d.count) || [0]))) },
          ].map(c => <div key={c.label} className="bg-white/4 rounded-lg py-2"><p className="text-white font-bold text-sm">{c.v}</p><p className="text-white/25 text-[9px]">{c.label}</p></div>)}
        </div>
      </div>

      {/* ── DAU CHART ── */}
      <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div>
            <p className="text-white text-sm font-semibold">Daily Active Users</p>
            <p className="text-white/30 text-xs">Distinct users with completed transactions per day</p>
          </div>
          <div className="flex gap-1">
            {["7d", "30d", "90d"].map(rv => (
              <button key={rv} onClick={() => setUaRange(rv)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all ${uaRange === rv ? "bg-white/10 text-white" : "text-white/30 hover:text-white/50"}`}>
                {rv}
              </button>
            ))}
          </div>
        </div>
        <div className="px-5 pt-4 pb-4">
          {uaLoading
            ? <div className="h-16 bg-white/5 rounded-xl animate-pulse" />
            : <MiniBarChart data={ua.dau || []} color="#00C896" valueKey="dau" height={56} />
          }
          <div className="flex items-center justify-between mt-1">
            <p className="text-white/20 text-[10px]">{ua.dau?.[0]?._id || ""}</p>
            <p className="text-white/20 text-[10px]">{ua.dau?.at?.(-1)?._id || ""}</p>
          </div>
        </div>
      </div>

      {/* ── USER BREAKDOWN ROWS ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

        {/* KYC Funnel */}
        <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
          <p className="text-white/40 text-[10px] uppercase tracking-wide px-4 py-3 border-b border-white/5">KYC Funnel</p>
          <div className="p-4 space-y-3">
            {kycFunnel.length === 0
              ? <p className="text-white/20 text-xs text-center py-4">No data</p>
              : kycFunnel.map(item => (
                <HBar key={item._id} label={KYC_LABELS[item._id] || item._id} value={item.count} total={kycTotal} color={KYC_COLORS[item._id] || "#64748B"} />
              ))
            }
          </div>
        </div>

        {/* Auth Provider Split */}
        <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
          <p className="text-white/40 text-[10px] uppercase tracking-wide px-4 py-3 border-b border-white/5">Sign-up Method</p>
          <div className="p-4">
            {bd.authProvider?.length ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <DonutChart
                    slices={(bd.authProvider || []).map((p, i) => ({
                      value: p.count,
                      color: ["#00C896", "#4285F4", "#7C3AED"][i] || "#64748B",
                    }))}
                    size={72}
                  />
                  <div className="flex-1 space-y-2">
                    {(bd.authProvider || []).map((p, i) => {
                      const colors = ["#00C896", "#4285F4", "#7C3AED"];
                      const provTotal = (bd.authProvider || []).reduce((s, x) => s + x.count, 0);
                      return (
                        <div key={p._id} className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: colors[i] || "#64748B" }} />
                          <p className="text-white/60 text-xs flex-1 capitalize">{p._id || "email"}</p>
                          <p className="text-white text-xs font-bold">{fmt(p.count)}</p>
                          <p className="text-white/30 text-[10px] w-8 text-right">{provTotal > 0 ? Math.round(p.count / provTotal * 100) : 0}%</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : <p className="text-white/20 text-xs text-center py-6">No data</p>}
          </div>
        </div>

        {/* Usage split: bills only / crypto only / mixed */}
        <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
          <p className="text-white/40 text-[10px] uppercase tracking-wide px-4 py-3 border-b border-white/5">User Behaviour Split</p>
          <div className="p-4">
            {ua.usageSplit ? (() => {
              const { billsOnly, cryptoOnly, mixed } = ua.usageSplit;
              const total = (billsOnly || 0) + (cryptoOnly || 0) + (mixed || 0);
              return (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <DonutChart
                      slices={[
                        { value: billsOnly || 0,  color: "#D97706" },
                        { value: cryptoOnly || 0, color: "#7C3AED" },
                        { value: mixed || 0,      color: "#00C896" },
                      ]}
                      size={72}
                    />
                    <div className="flex-1 space-y-2">
                      {[
                        { label: "Bills Only",   value: billsOnly  || 0, color: "#D97706" },
                        { label: "Crypto Only",  value: cryptoOnly || 0, color: "#7C3AED" },
                        { label: "Mixed (both)", value: mixed      || 0, color: "#00C896" },
                      ].map(s => (
                        <div key={s.label} className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
                          <p className="text-white/60 text-xs flex-1">{s.label}</p>
                          <p className="text-white text-xs font-bold">{fmt(s.value)}</p>
                          <p className="text-white/30 text-[10px] w-8 text-right">{total > 0 ? Math.round(s.value / total * 100) : 0}%</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })() : <p className="text-white/20 text-xs text-center py-6">Loading…</p>}
          </div>
        </div>
      </div>

      {/* ── GEOGRAPHY ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <SectionLabel label="Geography" />
          {uaLoading && <div className="w-4 h-4 border-2 border-white/10 border-t-white/40 rounded-full animate-spin" />}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Top states by users */}
          <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
            <p className="text-white/40 text-[10px] uppercase tracking-wide px-4 py-3 border-b border-white/5">
              Top States by Users
            </p>
            {(bd.topStates?.length || 0) === 0 && !uaLoading ? (
              <div className="flex flex-col items-center justify-center py-8 text-white/20">
                <i className="ri-map-pin-line text-2xl mb-1" />
                <p className="text-xs">No location data yet</p>
                <p className="text-[10px] mt-1 text-white/15">Populated once users set their location</p>
              </div>
            ) : (
              <div className="divide-y divide-white/4">
                {(bd.topStates || []).map((s, i) => (
                  <div key={s._id} className="flex items-center gap-3 px-4 py-2.5">
                    <span className="text-white/20 font-mono text-[10px] w-4">#{i + 1}</span>
                    <p className="text-white/60 text-xs flex-1 truncate">{s._id}</p>
                    <p className="text-white text-xs font-bold">{fmt(s.count)}</p>
                    <p className="text-white/25 text-[10px] w-8 text-right">{u.total > 0 ? Math.round(s.count / u.total * 100) : 0}%</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top states by tx volume */}
          <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
            <p className="text-white/40 text-[10px] uppercase tracking-wide px-4 py-3 border-b border-white/5">
              Top States by Volume ({uaRange})
            </p>
            {(ua.topStatesByVolume?.length || 0) === 0 && !uaLoading ? (
              <div className="flex flex-col items-center justify-center py-8 text-white/20">
                <i className="ri-bar-chart-line text-2xl mb-1" />
                <p className="text-xs">No location data yet</p>
              </div>
            ) : (
              <div className="divide-y divide-white/4">
                {(ua.topStatesByVolume || []).map((s, i) => {
                  const totalVol = (ua.topStatesByVolume || []).reduce((acc, x) => acc + x.volume, 0);
                  return (
                    <div key={s._id} className="flex items-center gap-3 px-4 py-2.5">
                      <span className="text-white/20 font-mono text-[10px] w-4">#{i + 1}</span>
                      <p className="text-white/60 text-xs flex-1 truncate">{s._id}</p>
                      <p className="text-white text-xs font-bold">{fmtK(s.volume)}</p>
                      <p className="text-white/25 text-[10px] w-8 text-right">{totalVol > 0 ? Math.round(s.volume / totalVol * 100) : 0}%</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Top cities */}
        {ua.topCitiesByVolume?.length > 0 && (
          <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden mt-4">
            <p className="text-white/40 text-[10px] uppercase tracking-wide px-4 py-3 border-b border-white/5">
              Top Cities by Volume ({uaRange})
            </p>
            <div className="p-4 grid grid-cols-2 sm:grid-cols-5 gap-2">
              {(ua.topCitiesByVolume || []).slice(0, 10).map((c, i) => {
                const maxVol = ua.topCitiesByVolume[0]?.volume || 1;
                const pct    = Math.round(c.volume / maxVol * 100);
                return (
                  <div key={c._id} className="bg-white/4 rounded-xl p-3 text-center border border-white/5">
                    <p className="text-white/30 text-[9px] mb-1">#{i + 1}</p>
                    <p className="text-white font-semibold text-xs truncate">{c._id}</p>
                    <p className="text-[#00C896] text-xs font-bold mt-1">{fmtK(c.volume)}</p>
                    <p className="text-white/20 text-[9px]">{c.users} users</p>
                    <div className="h-1 bg-white/5 rounded-full mt-2 overflow-hidden">
                      <div className="h-full bg-[#00C896] rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── COHORT RETENTION ── */}
      {ua.cohorts?.length > 0 && (
        <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5">
            <p className="text-white text-sm font-semibold">Weekly Signup Cohorts</p>
            <p className="text-white/30 text-xs mt-0.5">% of each week's signups who made at least one transaction</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left px-5 py-2.5 text-white/30 font-medium">Week</th>
                  <th className="text-right px-4 py-2.5 text-white/30 font-medium">Signups</th>
                  <th className="text-right px-4 py-2.5 text-white/30 font-medium">Activated</th>
                  <th className="text-left px-4 py-2.5 text-white/30 font-medium w-32">Activation %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/4">
                {ua.cohorts.map(c => (
                  <tr key={c.week} className="hover:bg-white/3 transition-colors">
                    <td className="px-5 py-2.5 text-white/60 font-mono">{c.week}</td>
                    <td className="px-4 py-2.5 text-white text-right font-medium">{fmt(c.signups)}</td>
                    <td className="px-4 py-2.5 text-[#00C896] text-right font-bold">{fmt(c.activated)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${c.activationPct}%`, background: c.activationPct >= 60 ? "#00C896" : c.activationPct >= 30 ? "#D97706" : "#DC2626" }} />
                        </div>
                        <span className={`text-[10px] font-bold w-8 text-right ${c.activationPct >= 60 ? "text-[#00C896]" : c.activationPct >= 30 ? "text-amber-400" : "text-red-400"}`}>
                          {c.activationPct}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── BILLS + REWARDS BREAKDOWN ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
          <p className="text-white/40 text-[10px] uppercase tracking-wide px-4 py-3 border-b border-white/5">Bills Breakdown ({range})</p>
          {aLoading ? <div className="p-4 space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-8 bg-white/5 rounded animate-pulse" />)}</div>
            : !analytics?.breakdown?.bills?.length ? <div className="flex flex-col items-center justify-center py-8 text-white/20"><i className="ri-receipt-line text-2xl mb-1" /><p className="text-xs">No data</p></div>
            : <div className="divide-y divide-white/4">
              {analytics.breakdown.bills.map(b => {
                const BILL_COLORS = { AIRTIME: "#059669", DATA: "#0284C7", ELECTRICITY: "#D97706", TV: "#7C3AED" };
                const color = BILL_COLORS[(b._id || "").toUpperCase()] || "#64748B";
                const total = analytics.breakdown.bills.reduce((s, x) => s + x.total, 0);
                return (
                  <div key={b._id} className="flex items-center gap-3 px-4 py-3">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                    <p className="text-white/60 text-xs flex-1 capitalize">{(b._id || "Other").toLowerCase()}</p>
                    <p className="text-white/30 text-xs">{b.count}x</p>
                    <p className="text-white text-xs font-semibold">{fmtK(b.total)}</p>
                    <p className="text-white/25 text-[10px] w-8 text-right">{total > 0 ? Math.round(b.total / total * 100) : 0}%</p>
                  </div>
                );
              })}
            </div>
          }
        </div>
        <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
          <p className="text-white/40 text-[10px] uppercase tracking-wide px-4 py-3 border-b border-white/5">Rewards Breakdown ({range})</p>
          {aLoading ? <div className="p-4 space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-8 bg-white/5 rounded animate-pulse" />)}</div>
            : !analytics?.breakdown?.rewards?.length ? <div className="flex flex-col items-center justify-center py-8 text-white/20"><i className="ri-gift-line text-2xl mb-1" /><p className="text-xs">No data</p></div>
            : <div className="divide-y divide-white/4">
              {analytics.breakdown.rewards.map(rw => {
                const REWARD_COLORS = { cashback: "#059669", milestone: "#7C3AED", referral: "#0284C7", referral_signup: "#0284C7", streak: "#F97316", admin_bonus: "#64748B" };
                const color = REWARD_COLORS[rw._id] || "#64748B";
                const total = analytics.breakdown.rewards.reduce((s, x) => s + x.total, 0);
                return (
                  <div key={rw._id} className="flex items-center gap-3 px-4 py-3">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                    <p className="text-white/60 text-xs flex-1">{REWARD_LABEL[rw._id] || rw._id}</p>
                    <p className="text-white/30 text-xs">{rw.count}x</p>
                    <p className="text-white text-xs font-semibold">{fmtK(rw.total)}</p>
                    <p className="text-white/25 text-[10px] w-8 text-right">{total > 0 ? Math.round(rw.total / total * 100) : 0}%</p>
                  </div>
                );
              })}
            </div>
          }
        </div>
      </div>

      {/* ── TOP USERS ── */}
      {analytics?.topUsers?.length > 0 && (
        <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
          <p className="text-white/40 text-[10px] uppercase tracking-wide px-5 py-3 border-b border-white/5">Top Users by Volume ({range})</p>
          <div className="divide-y divide-white/4">
            {analytics.topUsers.map((u, i) => (
              <div key={u._id} className="flex items-center gap-4 px-5 py-3.5">
                <span className="text-white/20 font-mono text-xs w-4">#{i + 1}</span>
                <div className="w-7 h-7 rounded-full bg-[#00C896]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#00C896] text-[10px] font-bold">{(u.userInfo?.userName || u.userInfo?.email || "?")[0].toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{u.userInfo?.userName || "—"}</p>
                  <p className="text-white/30 text-xs truncate">{u.userInfo?.email}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-white font-bold text-sm">{fmtK(u.volume)}</p>
                  <p className="text-white/30 text-[10px]">{u.count} txns</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showReferral && <ReferralTreePanel onClose={() => setShowReferral(false)} />}
    </div>
  );
}
